import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { QUERY_KEYS, useMerchants } from '@/hooks/use-queries'
import type { CreateResponse, Merchant } from '@/lib/types'

import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { ChonkyButton } from '@/components/shared/chonky-button'
import { Plus, Copy, CheckCircle2 } from 'lucide-react'

const createUserSchema = z.object({
  merchantSlug: z.string().min(1, 'Selecione uma empresa'),
  name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  email: z.string().email('E-mail inválido'),
  password: z.string().min(8, 'Senha deve ter pelo menos 8 caracteres'),
})

type CreateUserForm = z.infer<typeof createUserSchema>

export function CreateUserDialog() {
  const [open, setOpen] = useState(false)
  const [credentials, setCredentials] = useState<{ email: string; password: string } | null>(null)
  const queryClient = useQueryClient()
  const { data: merchants } = useMerchants()

  const form = useForm<CreateUserForm>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      merchantSlug: '',
      name: '',
      email: '',
      password: '',
    },
  })

  const { mutate, isPending } = useMutation({
    mutationFn: (data: CreateUserForm) => api.backoffice.createUser(data),
    onSuccess: (response: CreateResponse) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.backoffice.users })

      const tempPassword = response.tempPassword
      if (tempPassword) {
        setCredentials({
          email: form.getValues('email'),
          password: tempPassword,
        })
        toast.success('Usuário criado com sucesso!')
      } else {
        const email = response.admin?.email
        toast.success('Usuário criado com sucesso!', {
          description: `Um e-mail com as credenciais foi enviado para ${email}.`,
        })
        form.reset()
        setOpen(false)
      }
    },
    onError: (error: Error) => {
      toast.error('Erro ao criar usuário', {
        description: error.message || 'Verifique os dados e tente novamente.',
      })
    },
  })

  const onSubmit = (data: CreateUserForm) => {
    mutate(data)
  }

  const handleClose = () => {
    setOpen(false)
    setCredentials(null)
    form.reset()
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(val) => {
        if (val) setOpen(true)
        else handleClose()
      }}
    >
      <SheetTrigger asChild>
        <Button>
          <Plus className="mr-2 h-4 w-4" />
          Novo Usuário
        </Button>
      </SheetTrigger>
      <SheetContent className="sm:max-w-lg">
        {credentials ? (
          <div className="py-6 flex flex-col items-center text-center space-y-4">
            <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center text-green-600 mb-2">
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <SheetHeader className="border-b-0 px-0 pt-0 pb-0">
              <SheetTitle className="text-center">Usuário criado com sucesso!</SheetTitle>
              <SheetDescription className="text-center">
                As credenciais abaixo foram geradas para o acesso.
              </SheetDescription>
            </SheetHeader>

            <div className="w-full bg-slate-50 p-4 rounded-lg border border-slate-200 space-y-3 mt-4">
              <div className="space-y-1 text-left">
                <label className="text-xs font-medium text-slate-500 uppercase">E-mail</label>
                <div className="flex items-center justify-between bg-white p-2 rounded border">
                  <span className="text-sm font-mono">{credentials.email}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => {
                      navigator.clipboard.writeText(credentials.email)
                      toast.success('E-mail copiado!')
                    }}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              </div>
              <div className="space-y-1 text-left">
                <label className="text-xs font-medium text-slate-500 uppercase">Senha</label>
                <div className="flex items-center justify-between bg-white p-2 rounded border">
                  <span className="text-sm font-mono">{credentials.password}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => {
                      navigator.clipboard.writeText(credentials.password)
                      toast.success('Senha copiada!')
                    }}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="w-full pt-4">
              <ChonkyButton onClick={handleClose} className="w-full">
                Concluir
              </ChonkyButton>
            </div>
          </div>
        ) : (
          <>
            <SheetHeader>
              <SheetTitle>Novo Usuário</SheetTitle>
              <SheetDescription>Adicione um administrador a um lojista existente.</SheetDescription>
            </SheetHeader>

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="flex flex-col flex-1 overflow-hidden"
              >
                <SheetBody className="space-y-4">
                  <FormField
                    control={form.control}
                    name="merchantSlug"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Empresa (Lojista)</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione a empresa" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {merchants?.map((merchant: Merchant) => (
                              <SelectItem key={merchant.id} value={merchant.slug}>
                                {merchant.name} ({merchant.slug})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nome Completo</FormLabel>
                        <FormControl>
                          <Input placeholder="Ex: Maria Silva" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>E-mail</FormLabel>
                          <FormControl>
                            <Input placeholder="maria@loja.com" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Senha Inicial</FormLabel>
                          <FormControl>
                            <Input type="password" placeholder="********" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </SheetBody>

                <SheetFooter>
                  <ChonkyButton variant="secondary" type="button" onClick={handleClose}>
                    Cancelar
                  </ChonkyButton>
                  <ChonkyButton type="submit" disabled={isPending}>
                    {isPending ? 'Criando e Enviando...' : 'Criar Usuário'}
                  </ChonkyButton>
                </SheetFooter>
              </form>
            </Form>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}
